/**
 * LinkMentionChip - React component for rendering @link mentions in tiptap editor
 *
 * Displays link mentions as styled chips with the @linkName format.
 * Based on MentionChipComponent pattern from RichMentionEditor.js
 */

import React, { memo } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { Box, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

const chipSx = {
  backgroundColor: '#E8F0FE',
  color: '#1976d2',
  padding: '2px 8px',
  borderRadius: '12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontWeight: 500,
  cursor: 'default',
  userSelect: 'none' as const,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  '&:hover': {
    backgroundColor: '#D2E3FC',
  },
};

const iconSx = {
  fontSize: '0.875rem',
  opacity: 0.7,
};

const tagMarkerSx = {
  color: '#666',
  fontSize: '0.75em',
  fontFamily: 'monospace',
  verticalAlign: 'baseline',
};

export const LinkMentionChip = memo(function LinkMentionChip({
  node,
}: NodeViewProps) {
  const { id, labelTag } = node.attrs;

  return (
    <NodeViewWrapper
      as="span"
      className="link-mention-wrapper"
      contentEditable={false}
      draggable
      style={{
        display: 'inline-block',
        margin: '0 2px',
        verticalAlign: 'baseline',
      }}
    >
      {labelTag && (
        <Box component="span" sx={tagMarkerSx}>{`<${labelTag}>`}</Box>
      )}
      <Box
        component="span"
        className="link-mention-chip"
        data-link-id={id}
        sx={chipSx}
      >
        <LinkIcon sx={iconSx} />
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 'inherit', fontSize: 'inherit' }}
        >
          @{id}
        </Typography>
      </Box>
      {labelTag && (
        <Box component="span" sx={tagMarkerSx}>{`</${labelTag}>`}</Box>
      )}
    </NodeViewWrapper>
  );
});

export default LinkMentionChip;
